import { Link } from "react-router-dom";
import { AppleLogo, GooglePlayLogo } from "@phosphor-icons/react";
import { BRAND, LogoMark } from "@/lib/brand";
import { PageError, PageSkeleton } from "@/components/site/PageStates";
import { currentLocale } from "@/i18n/translate";

// Title + accent (Instrument Serif italic, coral, never wraps). stack: accent on its own line.
export function Heading({ as: H = "h2", title, accent, stack = false, className = "s-h2", id, testId }) {
  return (
    <H id={id} className={className} data-testid={testId}>
      {title}{accent && (stack ? <><br /><span className="s-accent">{accent}</span></> : <> <span className="s-accent">{accent}</span></>)}
    </H>
  );
}

export function Chip({ children, testId = "chip" }) {
  return children ? <span className="s-chip" data-testid={testId}>{children}</span> : null;
}

// Page shell with loading/error states (the hero and sections are per page).
export function SitePage({ loading, error, onRetry, testId, children, className = "" }) {
  return (
    <div className={`mx-auto max-w-6xl px-5 pb-20 pt-10 sm:px-8 sm:pt-16 ${className}`} data-testid={testId}>
      {loading && <PageSkeleton />}
      {!loading && error && <PageError message={error} onRetry={onRetry} />}
      {!loading && !error && <div className="cw-rise">{children}</div>}
    </div>
  );
}

export function PageHero({ chip, title, accent, lead, center = false, testId = "page-hero", children }) {
  return (
    <header className={`max-w-3xl ${center ? "mx-auto text-center" : ""}`} data-testid={testId}>
      <Chip testId={`${testId}-chip`}>{chip}</Chip>
      <Heading as="h1" title={title} accent={accent} className="s-h1 mt-6" testId="page-title" />
      {lead && <p className="s-muted mt-6 text-lg leading-8" data-testid={`${testId}-lead`}>{lead}</p>}
      {children}
    </header>
  );
}

export const IconTile = ({ icon: Icon, size = 22 }) => <span className="s-tile" aria-hidden><Icon size={size} weight="bold" /></span>;

// The cracked glass-pane icon used everywhere Break my glass appears.
export function GlassIcon({ size = 22, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="3.5" />
      <path d="M13.5 10.5 9 3.5M13.5 10.5l7.5-2M13.5 10.5l-2.5 10.5M13.5 10.5l7.5 6.5M13.5 10.5 3 14" />
      <path d="m10.6 6.6 2.4-.4M17 9.4l.6 2.4M12.2 16l-3.1.4" opacity=".7" />
    </svg>
  );
}

// Coral gradient band with a big faint face at its right edge and a white pill.
export function Band({ title, accent, line, button, to = "/#waitlist", onClick, testId = "band" }) {
  if (!title) return null;
  return (
    <section className="s-band mt-24 px-7 py-12 sm:px-14 sm:py-16" data-testid={testId}>
      <img src={BRAND.crush} alt="" className="s-band-face" aria-hidden />
      <div className="relative max-w-xl">
        <Heading title={title} accent={accent} />
        {line && <p className="mt-4 text-lg leading-8 text-white/85">{line}</p>}
        {button && (onClick
          ? <button type="button" onClick={onClick} className="s-btn s-btn-white s-focus mt-8 h-12 px-7" data-testid={`${testId}-button`}>{button}</button>
          : <Link to={to} className="s-btn s-btn-white s-focus mt-8 h-12 px-7" data-testid={`${testId}-button`}>{button}</Link>)}
      </div>
    </section>
  );
}

// Official store badge artwork, self-hosted in /public/badges (no third-party image requests).
const OFFICIAL = {
  ios: { img: "/badges/app-store.svg", alt: "Download on the App Store" },
  android: { img: "/badges/google-play.png", alt: "Get it on Google Play" },
};
const SOON = {
  ios: { icon: AppleLogo, small: "Coming soon on the", big: "App Store" },
  android: { icon: GooglePlayLogo, small: "Coming soon on", big: "Google Play" },
};

// /api/public/site `stores`: live → official badge linked to the url; else our own "SOON" pill (not clickable).
export function StoreButtons({ stores, className = "", testId = "store-buttons" }) {
  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`} data-testid={testId}>
      {["ios", "android"].map((k) => {
        const s = stores?.[k];
        if (s?.live && s.url) return (
          <a key={k} href={s.url} target="_blank" rel="noreferrer" className="s-focus inline-flex rounded-[10px]" data-testid={`${testId}-${k}-live`}>
            <img src={OFFICIAL[k].img} alt={OFFICIAL[k].alt} className={k === "ios" ? "h-[52px] w-auto" : "-my-[10px] h-[72px] w-auto"} />
          </a>);
        const { icon: Icon, small, big } = SOON[k];
        return (
          <span key={k} role="link" aria-disabled="true" aria-label={`${big}, coming soon`} className="s-store" data-testid={`${testId}-${k}-soon`}>
            <Icon size={24} weight="fill" aria-hidden />
            <span><small>{small}</small><b>{big}</b></span>
            <span className="s-soon" aria-hidden>SOON</span>
          </span>);
      })}
    </div>
  );
}

// Top-menu pill: white on dark, black on light.
export function JoinWaitlistButton({ label, className = "", testId = "join-waitlist-button", onClick }) {
  if (!label) return null;
  return <Link to="/#waitlist" onClick={onClick} className={`s-btn s-btn-contrast s-focus ${className}`} data-testid={testId}>{label}</Link>;
}

// Logo: face + "crush wink" ("wink" in coral).
export function Wordmark({ testId = "site-logo" }) {
  return (
    <span className="inline-flex items-center gap-2" data-testid={testId}>
      <LogoMark className="h-8 w-8" testId={`${testId}-mark`} />
      <span className="text-[1.15rem] font-bold tracking-tight">crush <span className="s-coral">wink</span></span>
    </span>
  );
}

export const money = (n) => (n == null ? "" : new Intl.NumberFormat(currentLocale(), { style: "currency", currency: "USD" }).format(Number(n)));
